import { useState } from "react";

import { useTimeline } from "@web-app/api";
import { LoadingIcon } from "@web-app/components";
import { Order } from "@web-app/types";
import { OrderTimelineSchema } from "@web-app/utils/helpers";

import AssigneeActivity from "./AssigneeActivity";
import AttachmentsActivity from "./AttachmentsActivity";
import AutoAssignActivity from "./AutoAssignActivity";
import ConnectedWorkspaceActivity from "./ConnectedWorkspaceActivity";
import CustomFieldActivity from "./CustomFieldActivity";
import DispatchLogicActivity from "./DispatchLogicActivity";
import NoteActivity from "./NoteActivity";
import NotificationActivity from "./NotificationActivity";
import OrderCopyActivity from "./OrderCopyActivity";
import OrderEditActivity from "./OrderEditActivity";
import ReportActivity from "./ReportActivity";
import ScheduleTimeActivity from "./ScheduleTimeActivity";
import SmsNotificationActivityV2 from "./SmsNotificationActivity";
import StatusActivity from "./StatusActivity";

type OrderTimelinePropsTypes = {
  order: Order;
};

export default function OrderTimeline({ order }: OrderTimelinePropsTypes) {
  const [timelineLimit] = useState(10);

  const { data: timelineData, isSuccess } = useTimeline({
    orderId: order.id,
    timelineLimit,
  });

  const timelineTotalLength = timelineData?.length || 0;

  if (!isSuccess) {
    return (
      <div className="flex items-center justify-center mt-5 gap-2">
        <LoadingIcon className="w-3" />
        <span className="text-xs">Loading timeline...</span>
      </div>
    );
  }

  return (
    <>
      <ul role="list" className="space-y-6">
        {timelineData &&
          timelineData.map((item, itemIdx) => {
            // Check if entity is a custom field (starts with customField_)
            const isCustomField = item.entity.startsWith("customField_");

            if (
              OrderTimelineSchema[item.entity] ||
              item.entity === "report" ||
              item.entity === "notification" ||
              item.entity === "dispatchLogic" ||
              isCustomField
            ) {
              const showTimelineBar = itemIdx !== timelineTotalLength - 1;

              // Handle custom fields first
              if (isCustomField) {
                return (
                  <CustomFieldActivity
                    key={item.id}
                    item={item}
                    showTimelineBar={showTimelineBar}
                    timezone={order.timezone}
                    order={order}
                  />
                );
              }

              // Handle order COPY action separately
              if (item.entity === "order" && item.action === "COPY") {
                return (
                  <OrderCopyActivity
                    key={item.id}
                    item={item}
                    showTimelineBar={showTimelineBar}
                    timezone={order.timezone}
                  />
                );
              }

              switch (item.entity) {
                case "autoAssign":
                  return (
                    <AutoAssignActivity
                      key={item.id}
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "assigneeId":
                  return (
                    <AssigneeActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "statusNotes":
                  return (
                    <NoteActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "connectedWorkspaceId":
                  return (
                    <ConnectedWorkspaceActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "status":
                  return (
                    <StatusActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      orderType={order.type}
                      timezone={order.timezone}
                    />
                  );
                case "attachments":
                  return (
                    <AttachmentsActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "scheduledStartDateTime":
                case "scheduledEndDateTime":
                  return (
                    <ScheduleTimeActivity
                      item={item}
                      deliveryType={order?.deliveryTypeText}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "smsNotification":
                  return (
                    <SmsNotificationActivityV2
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "notification":
                  return (
                    <NotificationActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "dispatchLogic":
                  return (
                    <DispatchLogicActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "report":
                  return (
                    <ReportActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
                case "geoPickupDateTime":
                case "geoDropoffDateTime":
                  return;
                default:
                  return (
                    <OrderEditActivity
                      item={item}
                      showTimelineBar={showTimelineBar}
                      timezone={order.timezone}
                    />
                  );
              }
            }
          })}
      </ul>
    </>
  );
}
